import React, { Component, type ReactNode } from 'react';
import { captureException } from '../utils/sentry';
import {
  canAutoReloadForChunk,
  markChunkReloadInFlight,
  buildChunkReloadUrl,
  logChunkRecoveryBreadcrumb,
  getBuildVersion,
} from '../utils/chunkRecoveryPolicy';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  /** Number of silent auto-recovery attempts for this error */
  retryCount: number;
  /** Incremented on retry to force full remount of children via key change */
  mountKey: number;
  /** True while waiting for auto-retry (shows "Reconnecting…" UI) */
  isRetrying: boolean;
  /** True when chunk reload limit is exhausted — show red UI instead of neutral overlay */
  chunkReloadExhausted: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DIAGNOSTICS HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

type ErrorCategory = 'chunk' | 'auth_transient' | 'auth_hard' | 'network' | 'null_state' | 'unknown';

/** Classify the error into a likely cause bucket */
function classifyError(error: Error): ErrorCategory {
  const msg = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();

  // A) Chunk / dynamic import failures (deploy + stale SW)
  if (
    name === 'chunkloaderror' ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    return 'chunk';
  }

  // B) Auth — distinguish transient (token refresh) from hard (permission denied)
  if (msg.includes('permission-denied') || msg.includes('missing or insufficient permissions')) {
    return 'auth_hard'; // Rules/claims mismatch — not transient
  }
  if (
    msg.includes('auth/network-request-failed') ||
    msg.includes('auth/internal-error') ||
    msg.includes('auth/too-many-requests') ||
    msg.includes('token') ||
    msg.includes('not authenticated')
  ) {
    return 'auth_transient'; // Token refresh in-flight or temporary
  }

  // C) Network / Firestore unavailable
  if (
    msg.includes('unavailable') ||
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('timeout') ||
    msg.includes('err_internet_disconnected') ||
    msg.includes('load failed')
  ) {
    return 'network';
  }

  // D) Null-state crash (resume with stale/missing data)
  if (
    msg.includes('cannot read properties of undefined') ||
    msg.includes('cannot read properties of null') ||
    msg.includes('is not a function') ||
    msg.includes('undefined is not an object')
  ) {
    return 'null_state';
  }

  return 'unknown';
}

/** Generate a short error ID for user-facing display (not a full stack trace) */
function generateErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 6);
  return `${ts}-${rand}`;
}

/** Detect if running as installed PWA */
function isPwaStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
  } catch { return false; }
}

/** Build a structured diagnostic snapshot for logging */
function buildDiagnostics(error: Error, errorInfo: React.ErrorInfo | null, errorId: string) {
  const category = classifyError(error);
  const stackLines = (error.stack || '').split('\n').slice(0, 5).join('\n');

  // Service worker state
  let swState = 'n/a';
  let swWaiting = false;
  try {
    const sw = (navigator as any).serviceWorker;
    if (sw?.controller) {
      swState = `active: ${sw.controller.state}`;
    } else if (sw) {
      swState = 'no controller';
      swWaiting = true;
    }
  } catch { /* ignore */ }

  // Auth state (peek without importing Firebase)
  let authState = 'unknown';
  try {
    const auth = (window as any).__FIREBASE_AUTH__;
    if (auth) {
      authState = auth.currentUser ? `uid: ${auth.currentUser.uid.slice(0, 8)}...` : 'no user';
    }
  } catch { /* ignore */ }

  // Build version (Vite injects at build time)
  let buildVersion = 'unknown';
  try {
    buildVersion = (import.meta as any).env?.VITE_BUILD_VERSION
      || (import.meta as any).env?.VERCEL_GIT_COMMIT_SHA?.slice(0, 8)
      || 'unknown';
  } catch { /* ignore */ }

  return {
    errorId,
    category,
    error: {
      name: error.name,
      message: error.message.slice(0, 200),
      stack: stackLines,
    },
    route: window.location.pathname + window.location.hash,
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    authState,
    swState,
    swWaiting,
    isPwa: isPwaStandalone(),
    platform: navigator.userAgent.includes('iPhone') ? 'iOS'
      : navigator.userAgent.includes('Android') ? 'Android' : 'Desktop',
    buildVersion,
    timestamp: new Date().toISOString(),
    componentStack: errorInfo?.componentStack?.split('\n').slice(0, 5).join('\n') || 'n/a',
  };
}

/** Whether this error is recoverable by silent retry (reset + remount) */
function isAutoRecoverable(error: Error): boolean {
  const cat = classifyError(error);
  return cat === 'auth_transient' || cat === 'network' || cat === 'null_state';
}

/** Whether this error is a chunk/module load failure (needs hard reload) */
function isChunkError(error: Error): boolean {
  return classifyError(error) === 'chunk';
}

// ── Chunk reload guard: imported from chunkRecoveryPolicy.ts ──

// Max silent retries before showing error UI
const MAX_AUTO_RETRIES = 1;
const RETRY_DELAY_MS = 1500;

const IS_DEV = typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV;
const SHOW_DEBUG = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Error Boundary — catches React component errors and prevents blank screens.
 *
 * Recovery strategy:
 *   1. Chunk errors → auto-reload (deploy while backgrounded). Max 2 per 24h.
 *   2. Auth transient / network / null-state → silent retry once with full remount.
 *   3. Auth hard (permission-denied) → show error UI immediately with sign-out suggestion.
 *   4. Retry fails or unknown → show error UI with diagnostics.
 */
export class ErrorBoundary extends Component<Props, State> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private errorId: string = '';

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      mountKey: 0,
      isRetrying: false,
      chunkReloadExhausted: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.errorId = generateErrorId();
    const diag = buildDiagnostics(error, errorInfo, this.errorId);

    // Always log full diagnostics to console (actionable in all environments)
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('📋 Diagnostics (copy-paste friendly):\n', JSON.stringify(diag, null, 2));

    this.setState({ error, errorInfo });

    // ── Auto-recovery: chunk errors ──
    if (isChunkError(error)) {
      if (canAutoReloadForChunk()) {
        console.log('[ErrorBoundary] Chunk error — performing full cache-busted recovery');
        this.performChunkRecovery();
        return; // Recovery in progress — "Updating CoreTrak…" overlay is shown
      }
      console.warn('[ErrorBoundary] Chunk error — reload limit reached, showing update UI');
      this.setState({ chunkReloadExhausted: true });
      // Fall through to show error UI (with "update required" message)
    }

    // ── Auto-recovery: transient auth / network / null-state ──
    // Show "Reconnecting…" UI, wait briefly, then remount children via key change.
    if (isAutoRecoverable(error) && this.state.retryCount < MAX_AUTO_RETRIES) {
      // Longer delay for offline/network errors (network needs more time to reconnect)
      const delay = (diag.category === 'network' && !navigator.onLine) ? 3000 : RETRY_DELAY_MS;
      console.log(`[ErrorBoundary] Recoverable error (${diag.category}) — retrying in ${delay}ms (attempt ${this.state.retryCount + 1}/${MAX_AUTO_RETRIES})`);
      this.setState({ isRetrying: true });
      this.retryTimer = setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prev.retryCount + 1,
          mountKey: prev.mountKey + 1, // Force full remount of children
          isRetrying: false,
        }));
      }, delay);
      return;
    }

    // ── Not recoverable or retry exhausted — send to Sentry ──
    // Attach errorId to error for matching user screenshots to Sentry events
    if (this.errorId) {
      (error as any).errorId = this.errorId;
      (error as any).errorCategory = diag.category;
    }
    captureException(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    if (this.chunkRecoveryTimeout) {
      clearTimeout(this.chunkRecoveryTimeout);
    }
  }

  /**
   * Full chunk recovery: delete scoped caches, update SW, wait for controllerchange,
   * then cache-busted hard navigation. Only called when reload guard allows.
   */
  private chunkRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;

  private performChunkRecovery() {
    markChunkReloadInFlight();
    logChunkRecoveryBreadcrumb('ErrorBoundary');

    // 5s safety timeout — if recovery stalls, show manual button
    this.chunkRecoveryTimeout = setTimeout(() => {
      console.warn('[ErrorBoundary] Chunk recovery timeout — showing manual reload');
      this.setState({ chunkReloadExhausted: true });
    }, 5000);

    const doReload = () => {
      window.location.replace(buildChunkReloadUrl());
    };

    // Step 1: Delete scoped app caches
    const deleteCaches = async () => {
      try {
        const keys = await caches.keys();
        const appCaches = keys.filter(k =>
          k.includes('coretrak') || k.includes('workbox') || k.includes('sw-') || k.includes('vite')
        );
        await Promise.all(appCaches.map(k => caches.delete(k)));
        if (appCaches.length > 0) {
          console.log('[ErrorBoundary] Deleted app caches:', appCaches);
        }
      } catch (err) {
        console.warn('[ErrorBoundary] Cache deletion failed:', err);
      }
    };

    // Step 2: Update SW + SKIP_WAITING + wait for controllerchange
    const updateSW = async () => {
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        if (!reg) return;

        // Best-effort: trigger SW update check
        try { await reg.update(); } catch { /* ignore */ }

        // Short-circuit if controller is already present after update
        if (navigator.serviceWorker.controller && !reg.waiting) return;

        if (reg.waiting) {
          console.log('[ErrorBoundary] Activating waiting service worker');
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });

          // Wait for controllerchange (or 2s timeout)
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(resolve, 2000);
            navigator.serviceWorker.addEventListener('controllerchange', () => {
              clearTimeout(timeout);
              console.log('[ErrorBoundary] SW controllerchange fired');
              resolve();
            }, { once: true });
          });
        }
      } catch (err) {
        console.warn('[ErrorBoundary] SW update failed:', err);
      }
    };

    // Execute: caches + SW → reload
    deleteCaches()
      .then(() => updateSW())
      .then(() => doReload())
      .catch(() => doReload()); // If anything fails, reload anyway
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      mountKey: prev.mountKey + 1,
      isRetrying: false,
      chunkReloadExhausted: false,
    }));
  };

  render() {
    // ── Chunk error: neutral "Updating…" overlay while auto-reload happens ──
    // Only shown while reload is still allowed. Once exhausted, falls through to red UI.
    if (this.state.hasError && this.state.error && isChunkError(this.state.error) && !this.state.chunkReloadExhausted) {
      const hasController = !!(navigator.serviceWorker?.controller);
      return (
        <div className="flex min-h-screen items-center justify-center bg-white p-4">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#007c86]" />
            <p className="text-sm font-medium text-gray-700">Updating CoreTrak…</p>
            <p className="text-xs text-gray-400 mt-1">This will only take a moment</p>
            <p className="text-[10px] text-gray-300 mt-2 font-mono">
              {window.location.pathname} · {getBuildVersion()} · sw:{hasController ? 'yes' : 'no'}
            </p>
          </div>
        </div>
      );
    }

    // ── Reconnecting state (during auto-retry delay) ──
    if (this.state.isRetrying) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white p-4">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
            <p className="text-sm text-gray-500">Reconnecting…</p>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const diag = this.state.error
        ? buildDiagnostics(this.state.error, this.state.errorInfo, this.errorId)
        : null;

      const isChunk = this.state.error ? isChunkError(this.state.error) : false;
      const isAuthHard = this.state.error ? classifyError(this.state.error) === 'auth_hard' : false;

      // Context-specific user message
      let userMessage = "We're sorry, but something unexpected happened. Please try refreshing the page.";
      if (isChunk) {
        userMessage = "An update is available. Please reload to continue.";
      } else if (isAuthHard) {
        userMessage = "There's a permissions issue with your account. Try signing out and back in.";
      } else if (diag?.category === 'network' && !navigator.onLine) {
        userMessage = "You appear to be offline. Please reconnect and try again.";
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#f9f3f0] to-white p-6">
          <div className="text-center max-w-xs w-full">
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, color: '#007c86', letterSpacing: -0.5, marginBottom: 24 }}>
              CoreTrak
            </div>
            <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-[#007c86]/20 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007c86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <p className="text-sm font-medium text-[#141517] mb-1">
              {isChunk ? 'Update available' : 'Something went wrong'}
            </p>
            <p className="text-xs text-[#9CA3AF] mb-6 leading-relaxed">
              {userMessage}
            </p>
            {this.errorId && (
              <p className="text-[10px] text-[#d1d5db] font-mono mb-4">
                {this.errorId}
              </p>
            )}
            {(IS_DEV || SHOW_DEBUG) && diag && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">
                  Error details
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-700 max-h-40">
{`Category: ${diag.category}
Error: ${diag.error.name}: ${diag.error.message}
Route: ${diag.route}
Online: ${diag.online}
Auth: ${diag.authState}
Build: ${diag.buildVersion}
Retries: ${this.state.retryCount}/${MAX_AUTO_RETRIES}

Stack:
${diag.error.stack}

Component:
${diag.componentStack}`}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button
                onClick={this.handleReload}
                className="flex-1 rounded-full py-3 text-sm font-semibold text-white"
                style={{ background: '#007c86' }}
              >
                {isChunk ? 'Update & Reload' : 'Reload'}
              </button>
              <button
                onClick={this.handleReset}
                className="flex-1 rounded-full py-3 text-sm font-semibold border"
                style={{ borderColor: 'rgba(0,0,0,0.1)', color: '#374151' }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Wrap children with key to enable full remount on retry
    return <React.Fragment key={this.state.mountKey}>{this.props.children}</React.Fragment>;
  }
}
